import { CmsComponent, ModularBlockComponent } from '@commerce-webcomponents/ui-composite';
import { useSession } from '@nextgen-web-framework/all';
import { GetStaticPropsContext } from 'next/types';
import styles from './ContentDetail.module.scss';
import Layout from 'components/Layout/Layout';
import { CommonDataProps, ContentType, contentDetailType, Content, CMSTransformerDataType } from 'types/common';
import { getContentPageData } from 'utils/getEssentials';
import { sanitizationConfig } from 'utils/sanitizationUtils';
import useLivePreview from 'live-preview/useLivePreview';
import { TemplateType, getComponentStylesConfig } from 'utils/componentsStylingConfigs';
import SEOComponent from 'components/SEOComponent/SEOComponent';
import usePageContent from 'components/PageContent/usePageContent';
import PageContentErrorHandler from 'components/PageContent/PageContentErrorHandler';

interface ContentDetailsProps extends CommonDataProps {
  type?: ContentType;
  title?: string;
  tagline?: string;
  params?: CMSTransformerDataType;
  notFoundPageData?: CMSTransformerDataType;
}
const ContentDetail = (props: ContentDetailsProps) => {
  const { session } = useSession();
  const { data: initialData, params, type } = props;
  const enableFlag = ConfigService.getConfigs().NEXT_PUBLIC_ENABLE_AMWAY_RESTRICTION as string;

  let uid = '';
  if (props.type) {
    uid = contentDetailType[props.type]?.uid;
  }
  const contentData = usePageContent({
    initialData: initialData,
    locale: params?.locale as string,
    uId: uid,
    queryUrl: `/${type}/${params?.contentId[0]}`,
    enableFlag,
    userType: session.type,
  });
  const [pageData, seoState] = useLivePreview(contentData.data, contentData.data?.seo_config);

  const header = pageData?.layout?.header;
  const footer = pageData?.layout?.footer;

  const getContentComponentStylesConfig = (type?: ContentType) => {
    switch (type) {
      case Content.articles:
        return getComponentStylesConfig(TemplateType.ARTICLE_DETAIL);
      case Content.promotions:
        return getComponentStylesConfig(TemplateType.PROMOTION_DETAIL);
      case Content.resources:
        return getComponentStylesConfig(TemplateType.RESOURCE_DETAIL);
      default:
        return getComponentStylesConfig(TemplateType.RESOURCE_DETAIL);
    }
  };

  return (
    <PageContentErrorHandler content={contentData} notFoundData={props.notFoundPageData}>
      <Layout
        hasTopMobileHeader={true}
        headerProps={{
          hasAmwayLogo: false,
          headerElement: (
            <div className={styles['promotion-header']}>
              <div className={styles['tagline-style']}>{props?.tagline}</div>
              <div className={styles['title-style']}>{props?.title}</div>
            </div>
          ),
        }}
        header={header}
        footer={footer}
      >
        <div data-testid="content-detail-page-testid">
          <SEOComponent {...seoState} />
          {pageData?.hero_section && (
            <div data-testid="hero-banner-section" className={styles['content-layout']}>
              {' '}
              <div className={styles[`${props.type}-banner`]}>
                <CmsComponent componentKey={Object.keys(pageData?.hero_section)?.[0]} data={pageData?.hero_section} />{' '}
              </div>
            </div>
          )}

          {pageData?.body_section && (
            <div data-testid="body-section" className={styles['content-layout']}>
              {' '}
              <ModularBlockComponent
                sanitizeComponentsConfig={sanitizationConfig}
                componentStylesConfig={getContentComponentStylesConfig(props.type)}
                data={pageData?.body_section}
              />
            </div>
          )}
        </div>
      </Layout>
    </PageContentErrorHandler>
  );
};

const getContentDetail = async (context: GetStaticPropsContext, type: ContentType) => {
  const contentId = context.params?.contentId === undefined ? '' : `${context.params?.contentId[0]}`;
  const { data, notFoundData, showError } = await getContentPageData({
    params: context.params,
    uId: contentDetailType[type]?.uid,
    url: `/${type}/${contentId}`,
    slugName: 'contentId',
  });
  return {
    data,
    showError,
    notFoundData,
  };
};

export default ContentDetail;

export { getContentDetail };
